import { History } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import {
  PAINEL_ACTIVITY_RESOURCE_META,
  PainelActivityTimeline,
} from '@/components/painel/PainelActivityTimeline';
import { PainelPermissionsDialogShell } from '@/components/painel/permissions/painel-role-visuals';
import { PainelRocketScroll } from '@/components/painel/PainelRocketScroll';
import {
  getResourceActivityLog,
  type PainelActivityResource,
} from '@/lib/painel-activity-log';
import { cn } from '@/lib/utils';

type PainelContentHistoryModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  resource: PainelActivityResource;
  resourceId: string | null;
  resourceLabel: string;
};

export function PainelContentHistoryModal({
  open,
  onOpenChange,
  resource,
  resourceId,
  resourceLabel,
}: PainelContentHistoryModalProps) {
  const meta = PAINEL_ACTIVITY_RESOURCE_META[resource];
  const Icon = meta.icon;
  const entries =
    resourceId && open
      ? getResourceActivityLog(resource, resourceId, { order: 'asc', limit: 500 })
      : [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="max-h-[90vh] max-w-2xl overflow-hidden border-0 bg-[#030023] p-0 shadow-none"
      >
        <DialogTitle className="sr-only">
          Histórico de alterações — {resourceLabel}
        </DialogTitle>
        <PainelPermissionsDialogShell>
          <div className="border-b border-white/10 bg-blue-950/20 px-6 pb-5 pt-6 sm:px-8">
            <div className="flex items-start gap-4 pr-14">
              <span
                className={cn(
                  'inline-flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl ring-1',
                  'bg-gradient-to-br from-sky-500/22 to-indigo-700/10 ring-sky-400/30',
                  'shadow-[0_0_40px_-10px_rgba(56,189,248,0.28)]'
                )}
              >
                <History className="h-7 w-7 text-sky-200" aria-hidden />
              </span>
              <div className="min-w-0 flex-1">
                <p className="font-inter text-[10px] font-bold uppercase tracking-[0.18em] text-sky-300/55">
                  {meta.label}
                </p>
                <h2 className="mt-1 font-poppins text-xl font-bold text-white sm:text-2xl">
                  Histórico de alterações
                </h2>
                <p className="mt-1.5 truncate font-inter text-sm text-slate-400">{resourceLabel}</p>
              </div>
            </div>
          </div>

          <PainelRocketScroll
            className='max-h-[min(52vh,520px)]'
            viewportClassName='px-6 py-6 sm:px-8 scroll-smooth'
          >
            <div className="mb-4 flex items-center justify-between gap-3">
              <p className="font-inter text-xs text-slate-500">
                Registo completo desde a criação, com autor e data de cada alteração.
              </p>
              <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.04] px-2.5 py-1 font-poppins text-[11px] font-semibold text-slate-300">
                <Icon className="h-3.5 w-3.5" aria-hidden />
                {entries.length}
              </span>
            </div>

            <PainelActivityTimeline
              entries={entries}
              variant="resource"
              emptyMessage="Nenhuma alteração registada para este conteúdo ainda."
            />
          </PainelRocketScroll>
        </PainelPermissionsDialogShell>
      </DialogContent>
    </Dialog>
  );
}
